import React, { useState, useEffect, useCallback } from 'react';
import { sessionAPI } from '../../services/api';
import { useBranchStore, useAuthStore } from '../../store';
import { toast } from 'react-toastify';
import { guestIdentity, guestDetails } from '../../utils/guest';
import AuthBadge, { authMeta } from '../../components/AuthMethodBadge/AuthMethodBadge';
import './Sessions.css';

/* ─────────────────────────────────────────────────────────────────────── */

const Sessions = () => {
    const selectedBranch = useBranchStore((state) => state.selectedBranch);
    const { hasRole } = useAuthStore();
    const canDisconnect = hasRole('superadmin', 'tenant_admin', 'branch_admin', 'support');
    const canDelete     = hasRole('superadmin', 'tenant_admin', 'branch_admin');

    const [sessions,     setSessions]     = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [activeOnly,   setActiveOnly]   = useState(true);
    const [searchTerm,   setSearchTerm]   = useState('');
    const [filterMethod, setFilterMethod] = useState('all');
    const [lastUpdated,  setLastUpdated]  = useState(null);

    /* clean-up state */
    const [showCleanup,   setShowCleanup]   = useState(false);
    const [cleanupDays,   setCleanupDays]   = useState(30);
    const [cleaning,      setCleaning]      = useState(false);

    // Loaded when the page opens and when the branch or the view changes — not on a timer:
    // the ↻ button refreshes it.
    const fetchSessions = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (selectedBranch?._id) params.branchId = selectedBranch._id;

            const response = activeOnly
                ? await sessionAPI.getActive(params.branchId)
                : await sessionAPI.getAll(params);

            setSessions(response.data.data.sessions || []);
            setLastUpdated(new Date());
        } catch {
            toast.error('Failed to fetch sessions');
        } finally {
            setLoading(false);
        }
    }, [selectedBranch, activeOnly]);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    const handleDisconnect = async (sessionId) => {
        if (!window.confirm('Disconnect this device? It will have to sign in through the landing page again.')) return;
        try {
            const res = await sessionAPI.disconnect(sessionId);
            // Said plainly when the WiFi gateway did not confirm: the device may still be
            // online until it next has to sign in.
            if (res.data?.gateway && res.data.gateway !== 'ack') toast.warning(res.data.message);
            else toast.success('Disconnected — the device will see the landing page again');
            fetchSessions();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to disconnect session');
        }
    };

    const handleCleanup = async () => {
        setCleaning(true);
        try {
            const res = await sessionAPI.cleanupOld({
                days: cleanupDays,
                branchId: selectedBranch?._id,
            });
            toast.success(res.data.message);
            setShowCleanup(false);
            if (!activeOnly) fetchSessions();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Cleanup failed');
        } finally {
            setCleaning(false);
        }
    };

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 KB';
        if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
        if (bytes >= 1048576)    return `${(bytes / 1048576).toFixed(2)} MB`;
        return `${(bytes / 1024).toFixed(2)} KB`;
    };

    const filteredSessions = sessions.filter(s => {
        const search = searchTerm.toLowerCase();
        const matchSearch =
            !search ||
            s.userId?.name?.toLowerCase().includes(search) ||
            guestIdentity(s.userId)?.label?.toLowerCase().includes(search) ||
            s.userId?.mobile?.includes(search) ||
            s.deviceMac?.toLowerCase().includes(search) ||
            s.framedIpAddress?.includes(search);
        const matchMethod = filterMethod === 'all' || s.authMethod === filterMethod;
        return matchSearch && matchMethod;
    });

    /* unique methods present in current session list for filter dropdown */
    const presentMethods = [...new Set(sessions.map(s => s.authMethod).filter(Boolean))];

    return (
        <div className="page-container-modern">

            {/* ── Header ─────────────────────────────────────────── */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-satellite-dish"></i> Sessions
                        </h1>
                        <p className="page-subtitle-modern">
                            {activeOnly
                                ? `Live connections${lastUpdated ? ` — as of ${lastUpdated.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}` : ''}`
                                : 'Historical connection log'}
                        </p>
                    </div>
                    <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
                        {/* Active / History toggle */}
                        <div className="flex bg-gray-200 p-1 rounded-lg">
                            <button
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeOnly ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                                onClick={() => setActiveOnly(true)}>
                                Active {activeOnly ? `(${sessions.length})` : ''}
                            </button>
                            <button
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${!activeOnly ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                                onClick={() => setActiveOnly(false)}>
                                History
                            </button>
                        </div>
                        <button className="btn-secondary-modern" onClick={fetchSessions} title="Refresh">
                            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                        </button>
                        {canDelete && (
                            <button className="btn-danger-modern btn-sm" onClick={() => setShowCleanup(true)} title="Remove old ended sessions">
                                <i className="fas fa-broom"></i> Clean Old
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Filters ─────────────────────────────────────────── */}
            <div className="filters-section" style={{ marginBottom:'1rem' }}>
                <div className="search-box-modern" style={{ flex:1 }}>
                    <i className="fas fa-search"></i>
                    <input type="text"
                        placeholder="Search by name, mobile, MAC, or IP…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)} />
                </div>
                <select className="filter-select"
                    value={filterMethod}
                    onChange={e => setFilterMethod(e.target.value)}>
                    <option value="all">All Auth Methods</option>
                    {presentMethods.map(m => {
                        const meta = authMeta(m);
                        return <option key={m} value={m}>{meta.label}</option>;
                    })}
                </select>
            </div>

            {/* ── Auth legend ──────────────────────────────────────── */}
            {sessions.length > 0 && presentMethods.length > 1 && (
                <div className="auth-legend">
                    {presentMethods.map(m => <AuthBadge key={m} method={m} small />)}
                </div>
            )}

            {/* ── Content ─────────────────────────────────────────── */}
            {filteredSessions.length === 0 && !loading ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className={`fas ${activeOnly ? 'fa-satellite-dish' : 'fa-history'}`}></i></div>
                    <h3>{activeOnly ? 'No Active Sessions' : 'No Session History'}</h3>
                    <p>{activeOnly
                        ? `No users are connected right now${filterMethod !== 'all' ? ` via ${authMeta(filterMethod).label}` : ''}.`
                        : 'Connection history will appear here once users authenticate.'}</p>
                </div>
            ) : (
                <div className="sessions-table-wrap">
                    <table className="sessions-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Auth</th>
                                <th>Plan</th>
                                <th>Duration</th>
                                <th>↑ Up</th>
                                <th>↓ Down</th>
                                <th>MAC / IP</th>
                                <th>Started</th>
                                <th>Status</th>
                                {canDisconnect && <th></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading
                                ? Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="st-skeleton-row">
                                        <td><div className="st-skel" style={{width:'70%'}} /></td>
                                        <td><div className="st-skel" style={{width:80}} /></td>
                                        <td><div className="st-skel" style={{width:60}} /></td>
                                        <td><div className="st-skel" style={{width:70}} /></td>
                                        <td><div className="st-skel" style={{width:50}} /></td>
                                        <td><div className="st-skel" style={{width:50}} /></td>
                                        <td><div className="st-skel" style={{width:120}} /></td>
                                        <td><div className="st-skel" style={{width:80}} /></td>
                                        <td><div className="st-skel" style={{width:50}} /></td>
                                        {canDisconnect && <td><div className="st-skel" style={{width:70}} /></td>}
                                    </tr>
                                ))
                                : filteredSessions.map(session => {
                                    const isLive = session.status === 'active';
                                    return (
                                        <tr key={session._id} className={`st-row${isLive ? ' st-row-live' : ''}`}>

                                            {/* User */}
                                            <td>
                                                <div className="st-user-cell">
                                                    {/* The guest — name, else room, else mobile (utils/guest): never
                                                        "Guest Room 302" or its stand-in "guest-…" mobile. */}
                                                    <div className="st-avatar">
                                                        {(guestIdentity(session.userId)?.label || 'G').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="st-user-name">{guestIdentity(session.userId)?.label || 'Guest User'}</div>
                                                        <div className="st-user-contact">{guestDetails(session.userId).join(' · ') || session.userId?.email || 'No contact'}</div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Auth method */}
                                            <td>
                                                {session.authMethod
                                                    ? <AuthBadge method={session.authMethod} small />
                                                    : <span className="st-muted">—</span>}
                                            </td>

                                            {/* Plan */}
                                            <td>
                                                <span className="st-plan">{session.planId?.name || '—'}</span>
                                            </td>

                                            {/* Duration */}
                                            <td>
                                                <span className="st-mono">{formatDuration(session.sessionDuration || 0)}</span>
                                            </td>

                                            {/* Upload */}
                                            <td>
                                                <span className="st-up">{formatBytes(session.dataUsage?.upload || 0)}</span>
                                            </td>

                                            {/* Download */}
                                            <td>
                                                <span className="st-down">{formatBytes(session.dataUsage?.download || 0)}</span>
                                            </td>

                                            {/* MAC + IP */}
                                            <td>
                                                <div className="st-mac-cell">
                                                    <code className="st-mac">{session.deviceMac || '—'}</code>
                                                    {session.framedIpAddress && <code className="st-ip">{session.framedIpAddress}</code>}
                                                </div>
                                            </td>

                                            {/* Started */}
                                            <td>
                                                <div className="st-time">
                                                    {session.startTime
                                                        ? new Date(session.startTime).toLocaleString('en-IN', { hour:'2-digit', minute:'2-digit', day:'2-digit', month:'short' })
                                                        : '—'}
                                                </div>
                                                {session.stopTime && (
                                                    <div className="st-time st-muted">
                                                        → {new Date(session.stopTime).toLocaleString('en-IN', { hour:'2-digit', minute:'2-digit', day:'2-digit', month:'short' })}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Status */}
                                            <td>
                                                {isLive ? (
                                                    <span className="st-live-badge">
                                                        <span className="st-live-dot"></span>
                                                        LIVE
                                                    </span>
                                                ) : (
                                                    <span className="st-ended-badge">Ended</span>
                                                )}
                                            </td>

                                            {/* Disconnect */}
                                            {canDisconnect && (
                                                <td>
                                                    {isLive && (
                                                        <button className="st-disconnect-btn"
                                                            onClick={() => handleDisconnect(session.sessionId)}>
                                                            <i className="fas fa-plug"></i> Disconnect
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            }
                        </tbody>
                    </table>
                </div>
            )}

            {/* ═══ CLEAN UP OLD SESSIONS MODAL ══════════════════ */}
            {showCleanup && (
                <div className="modal-overlay-modern" onClick={() => setShowCleanup(false)}>
                    <div className="modal-modern modal-small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2><i className="fas fa-broom" style={{ marginRight:8 }}></i>Clean Old Sessions</h2>
                            <button onClick={() => setShowCleanup(false)} className="modal-close">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <p style={{ fontSize:'0.875rem', color:'#6b7280', marginBottom:'1.25rem' }}>
                                Removes <strong>ended / stopped</strong> session records older than the selected number of days.
                                Active sessions are <strong>never</strong> deleted.
                            </p>
                            <div className="form-group-modern full-width">
                                <label>Remove sessions older than</label>
                                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                                    <input type="number" min="1" max="365"
                                        value={cleanupDays}
                                        onChange={e => setCleanupDays(Number(e.target.value))}
                                        style={{ width:'100px' }} />
                                    <span style={{ color:'#374151', fontSize:'0.875rem' }}>days</span>
                                </div>
                            </div>
                            <div className="cleanup-presets">
                                {[7, 30, 60, 90].map(d => (
                                    <button key={d} className={`preset-chip ${cleanupDays === d ? 'active' : ''}`}
                                        onClick={() => setCleanupDays(d)}>
                                        {d}d
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowCleanup(false)}>Cancel</button>
                            <button className="btn-danger-modern" disabled={cleaning} onClick={handleCleanup}>
                                {cleaning
                                    ? <><span className="spinner-sm"></span> Cleaning…</>
                                    : <><i className="fas fa-trash"></i> Delete Old Sessions</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sessions;
